import type { ReactNode, RefObject } from "react";
import {
  ArrowLeft,
  BookOpen,
  CalendarDays,
  Clock3,
  Layers3,
  MapPin,
  SlidersHorizontal,
  X,
} from "lucide-react";
import type { DateFilterType } from "@/components/FiltersPanel";
import type { TimePeriod } from "@/components/DateFilterPills";
import { cn } from "@/lib/utils";

export type ExploreMode = "events" | "sources" | "recently-viewed";

/** The date shortcuts shown in the discovery bar. "Tonight" is today limited to the evening. */
export type QuickDate = "today" | "tonight" | "weekend";

export function getActiveQuickDate(dateFilter: DateFilterType, timePeriods: TimePeriod[]): QuickDate | null {
  if (dateFilter === "weekend") return "weekend";
  if (dateFilter !== "today") return null;
  return timePeriods.length === 1 && timePeriods[0] === "evening" ? "tonight" : "today";
}

/**
 * Date and time state after choosing a shortcut (null clears the date).
 * Only Tonight owns the time of day: leaving Tonight resets time to all day,
 * while Today and This Weekend keep any time filter the visitor set themselves.
 */
export function applyQuickDate(
  state: { dateFilter: DateFilterType; timePeriods: TimePeriod[] },
  next: QuickDate | null,
): { dateFilter: DateFilterType; timePeriods: TimePeriod[] } {
  if (next === "tonight") return { dateFilter: "today", timePeriods: ["evening"] };
  const timePeriods: TimePeriod[] =
    getActiveQuickDate(state.dateFilter, state.timePeriods) === "tonight" ? ["allDay"] : state.timePeriods;
  return { dateFilter: next, timePeriods };
}

export interface DiscoveryFilterChip {
  id: string;
  label: string;
  onRemove?: () => void;
  onOpen?: () => void;
}

interface UnifiedDiscoveryBarProps {
  heading: string;
  intro?: string;
  sourceCount: number;
  resultCount: number | null;
  resultCountLoading: boolean;
  filtersOpen: boolean;
  activeQuickDate: QuickDate | null;
  activeFilterCount: number;
  chips: DiscoveryFilterChip[];
  exploreMode: ExploreMode;
  recentlyViewedCount: number;
  filterButtonRef?: RefObject<HTMLButtonElement>;
  onQuickDateChange: (next: QuickDate | null) => void;
  onOpenFilters: () => void;
  onClearAll: () => void;
  onExploreModeChange: (mode: ExploreMode) => void;
  /** Mobile-only status block rendered immediately below the filter controls. */
  mobileFilterSummary?: ReactNode;
  /** Opens the week or weekend guide panel */
  onOpenGuide: (type: "week" | "weekend") => void;
  /** Quick town filters (Albany, Troy, ...): beside the date shortcuts from 768px up, in their own row on phones */
  regionShortcuts?: { name: string; active: boolean }[];
  onRegionShortcut?: (region: string) => void;
  /** The guide open right now, if any, so its button shows as active */
  activeGuide?: "week" | "weekend" | null;
  /**
   * @deprecated Use onOpenGuide("week") instead.
   * Kept for backward-compatibility with any existing consumers; internally
   * delegates to onOpenGuide("week").
   */
  onOpenWeeklyGuide?: () => void;
}

export function formatLocalCalendarCount(sourceCount: number): string {
  if (sourceCount >= 90) return "90+ local calendars";
  if (sourceCount === 1) return "1 local calendar";
  return `${sourceCount} local calendars`;
}

export function formatResultCount(resultCount: number | null, loading: boolean): string {
  if (loading || resultCount === null) return "Updating events…";
  return `${resultCount.toLocaleString()} ${resultCount === 1 ? "event" : "events"}`;
}

export function UnifiedDiscoveryBar({
  heading,
  intro,
  sourceCount,
  resultCount,
  resultCountLoading,
  filtersOpen,
  activeQuickDate,
  activeFilterCount,
  chips,
  exploreMode,
  recentlyViewedCount,
  filterButtonRef,
  onQuickDateChange,
  onOpenFilters,
  onClearAll,
  onExploreModeChange,
  mobileFilterSummary,
  onOpenGuide,
  onOpenWeeklyGuide,
  regionShortcuts,
  onRegionShortcut,
  activeGuide = null,
}: UnifiedDiscoveryBarProps) {
  // backward-compat shim
  const openGuide = onOpenGuide ?? ((type: "week" | "weekend") => {
    if (type === "week") onOpenWeeklyGuide?.();
  });

  if (exploreMode !== "events") {
    const isSources = exploreMode === "sources";
    const modeTitle = isSources ? "Browse local calendars" : "Recently viewed";
    const modeDescription = isSources
      ? `Explore the ${formatLocalCalendarCount(sourceCount)} that make On A Lark local.`
      : recentlyViewedCount > 0
        ? `${recentlyViewedCount} ${recentlyViewedCount === 1 ? "event" : "events"} saved from this session.`
        : "Events you open will appear here for easy return visits.";

    return (
      <section
        className="mx-3 mt-3 overflow-hidden rounded-[22px] border border-[#d9d0c4] bg-[#fffef9] shadow-[0_8px_26px_rgba(61,48,29,0.07)] md:mx-4 lg:mx-6"
        aria-labelledby="secondary-discovery-heading"
        data-testid="unified-discovery-secondary"
      >
        <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between md:px-5">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e6f2eb] text-[#17664f]">
              {isSources ? <Layers3 className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
            </span>
            <div className="min-w-0">
              <h1 id="secondary-discovery-heading" className="text-lg font-semibold tracking-[-0.015em] text-[#292721]">
                {modeTitle}
              </h1>
              <p className="mt-0.5 text-sm text-[#746d62]">{modeDescription}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onExploreModeChange("events")}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 self-start rounded-full border border-[#cfc6b7] bg-white px-4 text-sm font-semibold text-[#3e3a33] shadow-[0_2px_0_#cfc6b7] transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 sm:self-auto"
            data-testid="unified-back-to-events"
          >
            <ArrowLeft className="h-4 w-4" />
            All events
          </button>
        </div>
      </section>
    );
  }

  const resultLabel = formatResultCount(resultCount, resultCountLoading);
  const hasRemovableChips = chips.some((chip) => Boolean(chip.onRemove));
  const quickDates: { value: QuickDate; label: string; wideLabel?: string }[] = [
    { value: "today", label: "Today" },
    { value: "tonight", label: "Tonight" },
    { value: "weekend", label: "Weekend", wideLabel: "This Weekend" },
  ];

  return (
    <section
      className="mx-3 mt-3 overflow-hidden rounded-[22px] border border-[#d9d0c4] bg-[#fffef9] shadow-[0_8px_26px_rgba(61,48,29,0.07)] md:mx-4 lg:mx-6"
      aria-labelledby="unified-discovery-heading"
      data-testid="unified-discovery-bar"
    >
      {/* ── Row 1: heading + calendar discovery context (context is desktop-only so phones reach events sooner) ── */}
      <div className="border-b border-[#e9e2d7] bg-[#f5f8f3] px-4 py-2.5 md:px-5 md:py-3.5">
        <h1
          id="unified-discovery-heading"
          className="line-clamp-2 text-base font-semibold leading-snug tracking-[-0.015em] text-[#292721] md:line-clamp-1 md:text-xl"
        >
          {heading}
        </h1>
        {/* Calendar context line — count + browse action grouped together. On phones, Menu → Browse local calendars. */}
        <div className="mt-1 hidden flex-wrap items-center gap-x-2 gap-y-1 md:flex">
          <span className="inline-flex items-center rounded-full border border-[#cfe1d5] bg-[#e9f3ed] px-2 py-0.5 text-xs font-semibold text-[#22634f]">
            {formatLocalCalendarCount(sourceCount)}
          </span>
          <button
            type="button"
            onClick={() => onExploreModeChange("sources")}
            className="inline-flex min-h-[32px] items-center gap-1 rounded-full px-1 text-xs font-semibold text-[#17664f] underline-offset-4 transition-colors hover:text-[#0f4f3c] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            data-testid="unified-browse-calendars"
            aria-label="Browse local calendars"
          >
            <Layers3 className="h-3.5 w-3.5" aria-hidden="true" />
            Browse calendars
          </button>
        </div>
        {intro && (
          <p className="mt-1 hidden max-w-3xl text-sm text-[#746d62] sm:line-clamp-1">{intro}</p>
        )}
      </div>

      {/* ── Row 2: date quick-filters, main filter button, result count ── */}
      <div className="bg-white px-3 py-2.5 md:px-5 md:py-3">
        {/* Phones: the three date shortcuts share one row and Filters spans the row below. */}
        <div className="grid grid-cols-3 gap-1.5 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
          {quickDates.map(({ value, label, wideLabel }) => {
            const active = activeQuickDate === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => onQuickDateChange(active ? null : value)}
                aria-pressed={active}
                className={cn(
                  "inline-flex min-h-[44px] min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-1 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 max-[359px]:text-xs sm:px-3",
                  active
                    ? "border-[#1D9E75] bg-[#e5f3ec] text-[#105b45] shadow-[inset_0_0_0_1px_rgba(29,158,117,0.12)]"
                    : "border-[#d3cabd] bg-[#fffef9] text-[#3e3a33] hover:border-[#8fbb9f] hover:bg-[#f6faf6]",
                )}
                data-testid={`unified-date-${value}`}
              >
                {value === "today" && <CalendarDays className="hidden h-4 w-4 sm:block" aria-hidden="true" />}
                {wideLabel ? (
                  <>
                    <span className="sm:hidden">{label}</span>
                    <span className="hidden sm:inline">{wideLabel}</span>
                  </>
                ) : (
                  label
                )}
              </button>
            );
          })}
          {/* Town shortcuts on phones: one compact full-width row under the dates, sized to fit all four */}
          {regionShortcuts && regionShortcuts.length > 0 && onRegionShortcut ? (
            <div className="col-span-3 flex w-full items-center gap-1.5 md:hidden" role="group" aria-label="Show events in a town">
              <MapPin className="h-4 w-4 shrink-0 text-[#8a8175] max-[359px]:hidden" aria-hidden="true" />
              {regionShortcuts.map(({ name, active }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onRegionShortcut(name)}
                  aria-pressed={active}
                  className={cn(
                    "inline-flex min-h-[40px] min-w-0 flex-auto items-center justify-center whitespace-nowrap rounded-full border px-2 text-[13px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 max-[359px]:px-1.5 max-[359px]:text-xs",
                    active
                      ? "border-[#1D9E75] bg-[#e5f3ec] text-[#105b45] shadow-[inset_0_0_0_1px_rgba(29,158,117,0.12)]"
                      : "border-[#d3cabd] bg-[#fffef9] text-[#3e3a33] hover:border-[#8fbb9f] hover:bg-[#f6faf6]",
                  )}
                  data-testid={`unified-region-mobile-${name.toLowerCase()}`}
                >
                  {name}
                </button>
              ))}
            </div>
          ) : null}
          {/* Town shortcuts on desktop and tablet (from 768px): beside the date shortcuts */}
          {regionShortcuts && regionShortcuts.length > 0 && onRegionShortcut ? (
            <div className="hidden items-center gap-2 md:flex" role="group" aria-label="Show events in a town">
              <span className="mx-0.5 h-6 w-px bg-[#e2d9cc]" aria-hidden="true" />
              {regionShortcuts.map(({ name, active }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onRegionShortcut(name)}
                  aria-pressed={active}
                  className={cn(
                    "inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2",
                    active
                      ? "border-[#1D9E75] bg-[#e5f3ec] text-[#105b45] shadow-[inset_0_0_0_1px_rgba(29,158,117,0.12)]"
                      : "border-[#d3cabd] bg-[#fffef9] text-[#3e3a33] hover:border-[#8fbb9f] hover:bg-[#f6faf6]",
                  )}
                  data-testid={`unified-region-${name.toLowerCase()}`}
                >
                  <MapPin className={cn("h-3.5 w-3.5", active ? "text-[#16795e]" : "text-[#8a8175]")} aria-hidden="true" />
                  {name}
                </button>
              ))}
            </div>
          ) : null}
          <button
            ref={filterButtonRef}
            type="button"
            onClick={onOpenFilters}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-controls="event-filters-mobile event-filters-desktop"
            aria-label={
              activeFilterCount > 0
                ? `Filter events, ${activeFilterCount} active ${activeFilterCount === 1 ? "filter" : "filters"}`
                : "Filter events"
            }
            className="col-span-3 inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full border border-[#16795e] bg-[#16795e] px-3 text-sm font-semibold text-white shadow-[0_2px_0_#0d5944] transition-all hover:-translate-y-px hover:bg-[#126b53] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            data-testid="unified-filter-button"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-xs font-bold text-[#16795e]"
                aria-hidden="true"
              >
                {activeFilterCount}
              </span>
            )}
          </button>
          {/* Result count — status region, right-aligned. On phones the filter summary below carries the count. */}
          <p
            className="m-0 ml-auto hidden text-sm font-semibold text-[#655f55] md:block"
            aria-live="polite"
            aria-atomic="true"
            data-testid="unified-result-count"
          >
            {resultLabel}
          </p>
        </div>

        {mobileFilterSummary && (
          <div className="-mx-3 -mb-3 mt-3 border-t border-[#d9e8de] md:hidden">
            {mobileFilterSummary}
          </div>
        )}

        {chips.length > 0 && (
          <div
            className="mt-3 hidden md:flex flex-wrap items-center gap-2 border-t border-[#eee8df] pt-3"
            aria-label="Active filters"
          >
            {chips.map((chip) => {
              if (!chip.onRemove) {
                return (
                  <span
                    key={chip.id}
                    className="inline-flex min-h-8 items-center rounded-full border border-[#bfdbc9] bg-[#edf6f0] px-3 text-xs font-semibold text-[#185e49]"
                  >
                    {chip.label}
                  </span>
                );
              }

              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={chip.onRemove}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-[#bfdbc9] bg-[#edf6f0] px-3 text-xs font-semibold text-[#185e49] transition-colors hover:border-[#78b291] hover:bg-[#e2f1e8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
                  aria-label={`Remove ${chip.label} filter`}
                  data-testid={`unified-chip-${chip.id}`}
                >
                  <span>{chip.label}</span>
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              );
            })}
            {hasRemovableChips && (
              <button
                type="button"
                onClick={onClearAll}
                className="inline-flex min-h-[44px] items-center px-1 text-xs font-semibold text-[#746d62] underline underline-offset-4 transition-colors hover:text-[#3e3a33] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
                data-testid="unified-clear-all"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Row 3: Weekend guides — desktop only. Phones get GuideShortcuts inside the event list instead. ── */}
      <div className="hidden items-center gap-2 border-t border-[#e9e2d7] bg-[#f5f8f3] px-5 py-3 md:flex">
        {([
          { type: "week", label: "What's On This Week", testId: "unified-week-guide" },
          { type: "weekend", label: "Weekend Summary", testId: "unified-weekend-summary" },
        ] as const).map(({ type, label, testId }) => {
          const active = activeGuide === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => openGuide(type)}
              aria-haspopup="dialog"
              aria-expanded={active}
              className={cn(
                "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2",
                active
                  ? "border-[#1D9E75] bg-[#e5f3ec] text-[#105b45] shadow-[inset_0_0_0_1px_rgba(29,158,117,0.12)]"
                  : "border-[#cfe1d5] bg-white text-[#17664f] hover:border-[#8fbb9f] hover:bg-[#f0f7f2]",
              )}
              data-testid={testId}
            >
              <BookOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
